import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, ViewChild, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    AsyncValidatorFn,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
    NgControl,
    NgForm,
    ReactiveFormsModule,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Nomenclature } from '@app/core/models/nomenclature.model';

import { NomenclatureService, Operateur, OperateurGeneral, Predicats } from '@app/shared/nomenclature/nomenclature.service';
import { BacASable, Espace, Periode } from '@pcscol/odf-api-v1-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, delay, distinctUntilChanged, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { FormMatErrorsDirective } from '../../base-form/form-mat-errors/form-mat-errors.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';

/**
 * Besoin d'un errorStateMatcher Custom pour afficher les erreurs même si l'erreur de nomenclature vient du back
 * (puisque le back accepte n'importe quoi)
 */
export class SelectNomenclatureErrorStateMatcher extends ErrorStateMatcher {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isErrorState(control: AbstractControl | null, _form: FormGroupDirective | NgForm | null): boolean {
        return !!control && control.invalid;
    }
}

@Component({
    selector: 'app-select-nomenclature',
    imports: [
        ReactiveFormsModule,
        MatTooltip,
        MatAutocompleteModule,
        MatInputModule,
        CommonModule,
        MatIconModule,
        FormMatErrorsDirective,
        MatFormFieldModule,
        MatIconButton
    ],
    templateUrl: './select-nomenclature.component.html',
    styleUrls: ['./select-nomenclature.component.scss']
})
export class SelectNomenclatureComponent implements ControlValueAccessor, OnInit {
    ngControl = inject(NgControl, { optional: true, self: true })!;
    private nomenclatureService = inject(NomenclatureService);
    private fb = inject(FormBuilder);

    errorStateMatcher: ErrorStateMatcher;
    @ViewChild(FormMatErrorsDirective) formMatErrors: FormMatErrorsDirective;

    destroyRef$ = inject(DestroyRef);

    filteredNomenclatures: Observable<Nomenclature[]>;
    filteredNomenclaturesAsList: Nomenclature[] = [];

    nomenclatureIsEmpty$: Observable<boolean>;
    readonly label = input.required<string>();
    readonly formFieldStyles = input<object>();
    @Input() nomenclature: string;

    readonly rechercheSurCodeEtLibelleLong = input<boolean>(false);
    readonly contexteConsommation = input<Espace | string | Date | undefined | null>(null);
    readonly readonly = input<boolean>(false);
    readonly isParametrage = input<boolean>(false);
    readonly showResetButton = input<boolean>(true);
    readonly dataTfaIdentifier = input<string | undefined>('');
    readonly excludedValues = input<readonly string[]>([]);
    readonly getFullValueFn = input<(n: Nomenclature | undefined) => string>(n => `${n?.code} - ${n?.libelle}`);
    private readonly excludedValues$ = toObservable(this.excludedValues);

    _internalForm = this.fb.control('', { asyncValidators: [this.nomenclatureAsyncValidator()] });

    constructor() {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        if (this.control.validator) {
            if (this.control.hasValidator(Validators.required)) {
                this._internalForm.addValidators(Validators.required);
            }
            this._internalForm.addValidators(this.control.validator);
        }
        this.filteredNomenclatures = combineLatest([
            this._internalForm.valueChanges!.pipe(
                startWith(this.ngControl.control!.value ?? ''),
                delay(0),
                distinctUntilChanged((x, y) => x == y),
                switchMap(text => this.listerNomenclaturesAndDefaultIfNecessary(text, this._internalForm.value))
            ),
            this.excludedValues$
        ]).pipe(
            map(([value, excludedValues]: [Nomenclature[], readonly string[]]) =>
                value
                    .filter(v => v.code === this._internalForm.value || !excludedValues.includes(v.code))
                    .filter(
                        v =>
                            !this._internalForm.value ||
                            v.code.toLowerCase().includes(this._internalForm.value?.toLowerCase()) ||
                            v.libelle.toLowerCase().includes(this._internalForm.value?.toLowerCase()) ||
                            v.libelleLong?.toLowerCase().includes(this._internalForm.value?.toLowerCase())
                    )
                    .sort((a, b) =>
                        b.prioriteAffichage && a.prioriteAffichage
                            ? b.prioriteAffichage - a.prioriteAffichage
                            : b.libelle.localeCompare(a.libelle)
                    )
            ),
            tap((value: Nomenclature[]) => {
                this.filteredNomenclaturesAsList = value;
                if (this.formMatErrors && this._internalForm.value) {
                    this.formMatErrors.detectChanges();
                }
            })
        );

        this.nomenclatureIsEmpty$ = combineLatest([
            this.filteredNomenclatures.pipe(startWith([])),
            this._internalForm.valueChanges.pipe(startWith(''))
        ]).pipe(
            map(([list, value]: [Nomenclature[], string | null]) => list.length === 0 && !value),
            startWith(false),
            tap((vide: boolean) => {
                if (vide) {
                    this._internalForm.markAsDirty();
                }
            })
        );

        this.control.addAsyncValidators(this.nomenclatureAsyncValidator());
        this.control.updateValueAndValidity();
    }

    onChange: (_: unknown) => void = () => {};
    onTouched = () => {};

    writeValue(obj: string | null): void {
        // réception de la valeur
        if (obj) {
            // Si à l'init on reçoit une valuer du back, un utilise ce stateMatcher custom pour afficher l'erreur même si le formulaire est pristine
            // Sinon on ne fait rien
            this.errorStateMatcher = new SelectNomenclatureErrorStateMatcher();
        }
        this._internalForm.setValue(obj);
    }
    registerOnChange(fn: (_: unknown) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this._internalForm.disable();
        } else {
            this._internalForm.enable();
        }
    }

    /**
     * Permet d'afficher la liste des nomenclature du type demandé et y ajoute la valeur en cours d'édition
     * si il s'agit d'une nomenclature invalide/non visible.
     *
     * Ceci permet d'éditer les valeurs actuelles d'un objet maquette et de pouvoir modifier d'autres champs
     * tout en laissant la valeur de la nomenclature invalide inchangée.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private listerNomenclaturesAndDefaultIfNecessary(text: string | null, code: string | null): Observable<Nomenclature[]> {
        return this.rechercheSurCodeEtLibelleLong() ? this.listerNomenclaturesCodeLibelleLong(text) : this.listerNomenclatures(text);
    }

    private listerNomenclatures(text: string | null): Observable<Nomenclature[]> {
        const predicat = new Predicats();
        const date = this.getDateFromContexteConsommation();
        if (date != null) {
            predicat.add('dateDebutValidite', Operateur.LessOrEqual, date);
            predicat.add('dateFinValidite', Operateur.GreaterOrEqual, date);
            predicat.visible();
        }
        if (!this.isParametrage()) {
            return this.nomenclatureService.listNomenclatures(
                this.nomenclature,
                undefined,
                { size: 20 },
                predicat.add('code', Operateur.Re, text ?? '')
            );
        } else {
            return this.nomenclatureService.listParametrages(this.nomenclature, undefined, { size: 20 }, predicat);
        }
    }

    /**
     * Méthode ajoutée pour rechercher sur code ET libellé long. TODO : généaliser si possible, après avoir vérifié les impacts
     */
    private listerNomenclaturesCodeLibelleLong(text: string | null): Observable<Nomenclature[]> {
        const predicat = new Predicats();
        const date = this.getDateFromContexteConsommation();
        if (date != null) {
            predicat.add('dateDebutValidite', Operateur.LessOrEqual, date);
            predicat.add('dateFinValidite', Operateur.GreaterOrEqual, date);
            predicat.visible();
        }
        if (!this.isParametrage()) {
            return this.nomenclatureService.listNomenclatures(
                this.nomenclature,
                undefined,
                { size: 20 },
                predicat.add('code', Operateur.Re, text ?? '').add('libelleAffichage', Operateur.Re, text ?? ''),
                OperateurGeneral.Ou
            );
        } else {
            return this.nomenclatureService.listParametrages(this.nomenclature, undefined, { size: 20 }, predicat);
        }
    }

    private getDateFromContexteConsommation(): string | null {
        const contexte = this.contexteConsommation();
        if (contexte == null) {
            return null;
        } else if (typeof contexte === 'string') {
            return contexte;
        } else if (this.isBacASable(contexte)) {
            return contexte.dateConsommationNomenclatures;
        } else if (this.isPeriode(contexte)) {
            return contexte.validite.dateDebut;
        } else if (this.isDate(contexte)) {
            return contexte.toISOString();
        } else {
            throw new Error(`Type de contexte de consommation non géré: ${contexte}`);
        }
    }

    private isDate(contexte: BacASable | Espace | null | undefined | string | Date): contexte is Date {
        return (contexte as Date)?.getDate() != null;
    }

    private isBacASable(contexte: BacASable | Espace | null | undefined | string | Date): contexte is BacASable {
        return (contexte as BacASable)?.dateConsommationNomenclatures != null;
    }

    private isPeriode(contexte: BacASable | Espace | null | undefined | string | Date): contexte is Periode {
        return (contexte as Periode)?.validite?.dateDebut != null;
    }

    reset() {
        this.control?.reset();
        this.control?.markAsDirty();
        this._internalForm?.reset();
        this._internalForm?.markAsDirty();
    }

    getFullValue(nomenclature: Nomenclature[]): (value: string) => string {
        return (value: string) => {
            if (value) {
                if (nomenclature.find(st => st.code === value)) {
                    const selectedValue = nomenclature.find(st => st.code === value);
                    return this.getFullValueFn()(selectedValue);
                } else {
                    return value;
                }
            } else {
                return '';
            }
        };
    }

    getLibelle(nomenclature: Nomenclature[]): (value: string | null) => string {
        return (value: string | null) => {
            if (value) {
                if (nomenclature.find(st => st.code === value)) {
                    const selectedValue = nomenclature.find(st => st.code === value);
                    return selectedValue?.libelleLong ?? '';
                } else {
                    return value;
                }
            } else {
                return '';
            }
        };
    }

    onSelect(e: MatOptionSelectionChange) {
        this.onChange(e.source.value);
    }

    private nomenclatureAsyncValidator(): AsyncValidatorFn {
        return (ctrl: AbstractControl): Observable<ValidationErrors | null> => {
            if (!ctrl.value) {
                return of(null);
            }

            return this._internalForm.valueChanges.pipe(
                startWith(ctrl.value),
                distinctUntilChanged((x, y) => x == y),
                switchMap(value => {
                    if (!value || typeof value !== 'string') {
                        return of(null);
                    }
                    return this.listerNomenclaturesAndDefaultIfNecessary(value, value).pipe(
                        map((nomenclatures: Nomenclature[]) => {
                            if (nomenclatures.length === 0) {
                                return { vide: `Aucune valeur de ${this.label()} ne correspond.` };
                            }
                            if (nomenclatures.some(v => v.code === value)) {
                                return null;
                            } else {
                                return { vide: `Aucune valeur de ${this.label()} ne correspond à ce code.` };
                            }
                        }),
                        catchError(err => {
                            return of({ vide: err });
                        })
                    );
                }),
                take(1)
            );
        };
    }

    get parentControlAsFormGroup(): FormGroup {
        return this.ngControl.control?.parent as FormGroup;
    }

    get control() {
        return this.ngControl.control as FormControl<unknown>;
    }

    get errorState(): boolean {
        return this.control.errors !== null;
    }
}
